import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    input,
    OnInit,
    Signal,
    signal,
    viewChildren,
    WritableSignal,
} from '@angular/core';

import { MapboxStyle } from '@connect-services/client/sdk/api';
import {
    DEFAULT_ICON_HEIGHT_WIDTH,
    LegendInformationAndStyle,
    LegendService,
    SpriteInformationWithUrl,
} from '@connect-services/client/services/legend.service';
import { RessourcesModule } from '@ressources/client/ressources.module';

const RGB_ALPHA_LENGTH = 4;

const FULL_OPACITY = 1;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-legend',
    templateUrl: './legend.component.html',
})
export class LegendComponent implements OnInit, AfterViewInit {
    public readonly canvasRefs: Signal<readonly ElementRef<HTMLCanvasElement>[]> =
        viewChildren<ElementRef<HTMLCanvasElement>>('spriteCanvas');

    public readonly spriteInformationAndLegendStyles: WritableSignal<LegendInformationAndStyle[]> = signal([]);
    public readonly style = input<MapboxStyle[]>([]);

    protected readonly legendService = inject(LegendService);
    protected readonly MapboxStyle = MapboxStyle;
    protected readonly Object = Object;

    public ngAfterViewInit(): void {
        const canvases = this.canvasRefs();

        this.spriteInformationAndLegendStyles().forEach((spriteInformationAndStyle, index) => {
            if (!spriteInformationAndStyle.spriteInformationList.length) {
                return;
            }

            const canvas = canvases[index]?.nativeElement;

            if (!canvas) {
                return;
            }

            this.recolorImage(spriteInformationAndStyle, canvas);
        });
    }

    public ngOnInit(): void {
        const legendInformationAndStyleList: LegendInformationAndStyle[] = this.legendService.getLegendStyles(
            this.style(),
        );

        const noGroupedLegendList: LegendInformationAndStyle[] = legendInformationAndStyleList.filter(
            (legendInformationAndStyle: LegendInformationAndStyle) => !legendInformationAndStyle.group,
        );

        const legendStyleAndInformationByGroup: Record<string, LegendInformationAndStyle> =
            legendInformationAndStyleList.reduce(
                (acc: Record<string, LegendInformationAndStyle>, currentValue: LegendInformationAndStyle) => {
                    return this.groupStyleAndInformationStyle(acc, currentValue);
                },
                {},
            );

        this.spriteInformationAndLegendStyles.set([
            ...Object.values(legendStyleAndInformationByGroup).filter(
                (legendStyleAndInformation: LegendInformationAndStyle) =>
                    Object.values(legendStyleAndInformation.legendStyle.style).length,
            ),
            ...noGroupedLegendList.filter(
                (noGroupedLegend: LegendInformationAndStyle) => Object.values(noGroupedLegend.legendStyle.style).length,
            ),
        ]);
    }

    private colorSprite(
        data: Uint8ClampedArray,
        rgbParse: { b: number; g: number; r: number },
        iconOpacity?: number,
    ): void {
        for (let i = 0; i < data.length; i += RGB_ALPHA_LENGTH) {
            const alpha = data[i + 3];

            if (!alpha) {
                continue;
            }

            data[i] = rgbParse.r;
            data[i + 1] = rgbParse.g;
            data[i + 2] = rgbParse.b;
            data[i + 3] = Math.round(alpha * (iconOpacity ?? FULL_OPACITY));
        }
    }

    private drawIconOnCanvas(
        canvasContext: CanvasRenderingContext2D,
        tempCanvas: HTMLCanvasElement,
        isFillType: boolean,
        repeatCount: number,
        spriteHeight: number,
        spriteWidth: number,
        rotationDeg = 0,
    ): void {
        for (let i = 0; i < repeatCount; i++) {
            const x = i * spriteWidth;
            const y = 0;

            this.rotate(canvasContext, tempCanvas, x, y, spriteWidth, spriteHeight, rotationDeg);

            if (!isFillType) {
                continue;
            }

            this.rotate(canvasContext, tempCanvas, x, y + spriteHeight, spriteWidth, spriteHeight, rotationDeg);
        }
    }

    private drawLineOnCanvas(canvas: HTMLCanvasElement, spriteInformationAndStyle: LegendInformationAndStyle): void {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        if (
            spriteInformationAndStyle.legendStyle.type !== MapboxStyle.TypeEnum.LINE ||
            !spriteInformationAndStyle.lineColor
        ) {
            return;
        }

        ctx.beginPath();
        ctx.moveTo(0, canvas.height / 2);
        ctx.lineTo(canvas.width, canvas.height / 2);
        ctx.strokeStyle = spriteInformationAndStyle.lineColor;
        ctx.lineWidth = 2;
        ctx.stroke();
    }

    private groupStyleAndInformationStyle(
        groupedLegendStyleAndInformation: Record<string, LegendInformationAndStyle>,
        legendInformationAndStyle: LegendInformationAndStyle,
    ): Record<string, LegendInformationAndStyle> {
        const style: Record<string, unknown> = {};

        const groupName: string | undefined = legendInformationAndStyle.group;

        if (!groupName) {
            return groupedLegendStyleAndInformation;
        }

        if (!groupedLegendStyleAndInformation[groupName]) {
            groupedLegendStyleAndInformation[groupName] = {
                ...legendInformationAndStyle,
                color: [],
                group: groupName,
                name: legendInformationAndStyle.name,
                rotation: [],
                sizeRatio: [],
                spriteInformationList: [],
            };
        }

        if (legendInformationAndStyle.spriteInformationList.length) {
            groupedLegendStyleAndInformation[groupName].spriteInformationList.push(
                ...legendInformationAndStyle.spriteInformationList,
            );
            groupedLegendStyleAndInformation[groupName]!.color!.push(...(legendInformationAndStyle.color ?? []));
            groupedLegendStyleAndInformation[groupName]!.iconOpacity = Number(
                legendInformationAndStyle.legendStyle.style['opacity'] ?? 1,
            );
            groupedLegendStyleAndInformation[groupName]!.rotation!.push(...(legendInformationAndStyle.rotation ?? []));
            groupedLegendStyleAndInformation[groupName]!.sizeRatio!.push(
                ...(legendInformationAndStyle.sizeRatio ?? []),
            );
        } else {
            style['background-color'] = legendInformationAndStyle.legendStyle.style['background-color'];
            style['border'] = legendInformationAndStyle.legendStyle.style['border'];
            style['opacity'] = legendInformationAndStyle.legendStyle.style['opacity'];

            groupedLegendStyleAndInformation[groupName].legendStyle.style = style;
        }

        if (legendInformationAndStyle.lineColor) {
            groupedLegendStyleAndInformation[groupName].lineColor = legendInformationAndStyle.lineColor;
            groupedLegendStyleAndInformation[groupName].legendStyle.style['background-color'] = undefined;
        }

        if (
            legendInformationAndStyle.legendStyle.type === MapboxStyle.TypeEnum.FILL &&
            groupedLegendStyleAndInformation[groupName].spriteInformationList.length
        ) {
            groupedLegendStyleAndInformation[groupName].legendStyle.style['height'] =
                `${(groupedLegendStyleAndInformation[groupName].spriteInformationList?.[0].height ?? DEFAULT_ICON_HEIGHT_WIDTH) * 2}px`;

            groupedLegendStyleAndInformation[groupName].legendStyle.style['width'] =
                `${(groupedLegendStyleAndInformation[groupName].spriteInformationList?.[0].width ?? DEFAULT_ICON_HEIGHT_WIDTH) * 3}px`;
        }

        return groupedLegendStyleAndInformation;
    }

    private parseRGBString(rgb?: string): { b: number; g: number; r: number } | undefined {
        if (!rgb) {
            return undefined;
        }

        const regex = /rgb\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)/i;
        const match = rgb.match(regex);

        if (!match) {
            throw undefined;
        }

        return {
            b: parseFloat(match[3]),
            g: parseFloat(match[2]),
            r: parseFloat(match[1]),
        };
    }

    private recolorImage(spriteInformationAndStyle: LegendInformationAndStyle, canvas: HTMLCanvasElement): void {
        const canvasContext = canvas.getContext('2d');

        if (!canvasContext) {
            return;
        }

        const image = new Image();

        image.onload = (): void => {
            const repeatCount = spriteInformationAndStyle.legendStyle.type === MapboxStyle.TypeEnum.SYMBOL ? 1 : 3;
            const spriteWidth = DEFAULT_ICON_HEIGHT_WIDTH;
            const spriteHeight = DEFAULT_ICON_HEIGHT_WIDTH;

            canvas.width = repeatCount * spriteWidth + (repeatCount - 1);
            canvas.height =
                spriteHeight * (spriteInformationAndStyle.legendStyle.type === MapboxStyle.TypeEnum.FILL ? 2 : 1);

            spriteInformationAndStyle.spriteInformationList.forEach((frame, index) => {
                let rgbParse: { b: number; g: number; r: number } | undefined;
                // parse rgb only when sdf is true because otherwise the color and halo information aren't taken into account on map libre
                if (
                    spriteInformationAndStyle.spriteInformationList.some((value: SpriteInformationWithUrl) => value.sdf)
                ) {
                    rgbParse = this.parseRGBString(spriteInformationAndStyle.color![index]);
                }

                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = spriteWidth;
                tempCanvas.height = spriteHeight;
                const tempCtx = tempCanvas.getContext('2d');

                if (!tempCtx) {
                    return;
                }

                const sizeRatio: number = spriteInformationAndStyle.sizeRatio![index];

                const dw = spriteWidth * sizeRatio;
                const dh = spriteHeight * sizeRatio;

                const cx = tempCanvas.width / 2;
                const cy = tempCanvas.height / 2;

                const dx = cx - dw / 2;
                const dy = cy - dh / 2;

                tempCtx.drawImage(image, frame.x, frame.y, spriteWidth, spriteHeight, dx, dy, dw, dh);

                const imageData = tempCtx.getImageData(0, 0, spriteWidth, spriteHeight);
                const data = imageData.data;

                if (rgbParse) {
                    this.colorSprite(data, rgbParse, spriteInformationAndStyle.iconOpacity);
                }

                tempCtx.putImageData(imageData, 0, 0);

                const rotation: number = spriteInformationAndStyle.rotation![index];

                this.drawIconOnCanvas(
                    canvasContext,
                    tempCanvas,
                    spriteInformationAndStyle.legendStyle.type === MapboxStyle.TypeEnum.FILL,
                    repeatCount,
                    spriteHeight,
                    spriteWidth,
                    rotation,
                );
            });

            //This part draw the line for style with sprite and lines
            this.drawLineOnCanvas(canvas, spriteInformationAndStyle);
        };

        image.src = spriteInformationAndStyle.spriteInformationList[0].url;
    }

    private rotate(
        ctx: CanvasRenderingContext2D,
        source: CanvasImageSource,
        x: number,
        y: number,
        w: number,
        h: number,
        rotationDegrees = 0,
    ): void {
        if (!rotationDegrees) {
            ctx.drawImage(source, x, y, w, h);

            return;
        }

        const radian: number = (rotationDegrees * Math.PI) / 180;

        ctx.save();

        const cx: number = x + w / 2;
        const cy: number = y + h / 2;

        ctx.translate(cx, cy);
        ctx.rotate(radian);
        ctx.drawImage(source, -w / 2, -h / 2, w, h);

        ctx.restore();
    }
}
